<template>
  <div
    v-if="state.open"
    class="fixed top-0 bottom-0 left-0 right-0 z-50 pb-12"
    style="background: rgba(0, 0, 0, 0.3); overflow-y: auto"
  >
    <div class="mx-auto container">
      <div class="bg-white rounded shadow p-2 border border-gray-100 opacity-100 mt-12">
        <div class="flex flex-row items-center">
          <p class="p-2 font-medium flex-grow text-primary">
            Propose Template Tags
          </p>
          <button
            @click="close"
            class="p-2 text-gray-500 hover:text-gray-700"
            type="button"
          >
            ×
          </button>
        </div>

        <div v-if="state.loading" class="p-4 text-center">
          <p class="text-gray-600">{{ state.loadingMessage || 'Loading...' }}</p>
        </div>

        <div v-else-if="state.error" class="p-4">
          <div class="bg-red-50 border border-red-200 rounded p-3 text-red-800">
            {{ state.error }}
          </div>
          <button @click="close" class="mt-4 px-4 py-2 bg-gray-500 text-white rounded">
            Close
          </button>
        </div>

        <div v-else-if="!state.proposalActive" class="p-4">
          <p class="mb-4 text-gray-700">
            Select a sample document to propose template tags for
            <strong class="text-primary">{{ formTypeName }}</strong> revision
            <strong class="text-primary">{{ revisionNumber }}</strong>.
          </p>

          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Sample Document
            </label>
            <select
              v-model="state.selectedDocumentId"
              class="w-full border border-gray-300 rounded px-3 py-2"
              :disabled="state.documents.length === 0"
            >
              <option value="">{{ state.documents.length === 0 ? 'No matching documents found' : 'Select a sample document...' }}</option>
              <option
                v-for="doc in state.documents"
                :key="doc.id"
                :value="doc.id"
              >
                {{ doc.label }}
              </option>
            </select>
            <p class="mt-1 text-xs text-gray-500">
              Only documents with TypeID matching this form type are shown.
            </p>
          </div>

          <div class="flex gap-2">
            <button
              @click="startProposal"
              :disabled="!state.selectedDocumentId"
              class="px-4 py-2 bg-primary text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Propose Tags
            </button>
            <button
              @click="close"
              class="px-4 py-2 bg-gray-500 text-white rounded"
            >
              Cancel
            </button>
          </div>
        </div>

        <div v-else class="p-4">
          <div class="mb-4 bg-blue-50 border border-blue-200 rounded p-3">
            <p class="text-sm text-blue-800">
              Sample Document: <strong>{{ selectedDocumentLabel }}</strong>
            </p>
          </div>

          <div v-if="state.reviewData && state.reviewData.length > 0" class="mb-4">
            <div class="flex items-center justify-between mb-2">
              <h3 class="font-medium text-gray-900">Review Proposed Tags</h3>
              <div class="text-sm text-gray-600">
                {{ checkedRowsCount }} / {{ state.reviewData.length }} tags selected
              </div>
            </div>

            <div class="border border-gray-300 rounded overflow-auto" style="max-height: 400px;">
              <table class="w-full text-sm">
                <thead class="bg-gray-50 sticky top-0">
                  <tr>
                    <th class="px-2 py-2 text-left border-b">
                      <input
                        type="checkbox"
                        :checked="allChecked"
                        @change="toggleAll"
                        class="cursor-pointer"
                      />
                    </th>
                    <th class="px-2 py-2 text-left border-b">Find Text</th>
                    <th class="px-2 py-2 text-left border-b">Path</th>
                    <th class="px-2 py-2 text-left border-b">Sample Value</th>
                    <th class="px-2 py-2 text-left border-b">Confidence</th>
                    <th class="px-2 py-2 text-left border-b">Status</th>
                    <th class="px-2 py-2 text-left border-b">Part</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(row, index) in state.reviewData"
                    :key="index"
                    :class="{ 'bg-gray-50': !row.checked, 'bg-yellow-50': row.status === 'warning' }"
                  >
                    <td class="px-2 py-2 border-b">
                      <input
                        type="checkbox"
                        v-model="row.checked"
                        class="cursor-pointer"
                      />
                    </td>
                    <td class="px-2 py-2 border-b">
                      <code class="text-xs bg-gray-100 px-1 py-0.5 rounded">{{ row.findText }}</code>
                    </td>
                    <td class="px-2 py-2 border-b">
                      <select
                        v-model="row.fieldPath"
                        @change="onPathChange"
                        class="w-full border border-gray-300 rounded px-2 py-1 text-xs"
                        :disabled="!row.checked"
                      >
                        <option
                          v-for="allowedPath in state.allowlist"
                          :key="allowedPath"
                          :value="allowedPath"
                        >
                          {{ allowedPath }}
                        </option>
                      </select>
                    </td>
                    <td class="px-2 py-2 border-b text-xs">
                      {{ row.sampleValue || '—' }}
                    </td>
                    <td class="px-2 py-2 border-b text-xs">
                      {{ row.confidence != null ? row.confidence.toFixed(2) : '—' }}
                    </td>
                    <td class="px-2 py-2 border-b text-xs">
                      <span
                        :class="{
                          'text-green-700': row.status === 'matched' || row.status === 'applied',
                          'text-yellow-700': row.status === 'warning' || row.status === 'skipped',
                          'text-red-700': row.status === 'error' || row.status === 'rejected' || row.status === 'not-found',
                          'text-gray-600': !row.status
                        }"
                      >
                        {{ row.status || '—' }}
                      </span>
                    </td>
                    <td class="px-2 py-2 border-b text-xs">
                      {{ row.part || '—' }}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="mt-4 p-3 bg-gray-50 rounded text-sm">
              <p class="text-gray-700 mb-2"><strong>Legend:</strong></p>
              <ul class="text-xs text-gray-600 space-y-1">
                <li><strong>Applied:</strong> Tag successfully applied to template</li>
                <li><strong>Skipped:</strong> Tag already exists in template</li>
                <li><strong>Not-found:</strong> Find text not found in template</li>
                <li><strong>Rejected:</strong> Invalid path or validation error</li>
              </ul>
            </div>
          </div>

          <div v-else class="mb-4 p-4 bg-gray-50 border border-gray-200 rounded text-center text-gray-600">
            No tags to review.
          </div>

          <div class="flex gap-2 flex-wrap">
            <button
              @click="reapplyMapping"
              :disabled="state.loading"
              class="px-4 py-2 bg-blue-600 text-white rounded disabled:bg-gray-300"
            >
              Re-apply Mapping
            </button>
            <button
              @click="previewTemplate"
              :disabled="state.loading"
              class="px-4 py-2 bg-green-600 text-white rounded disabled:bg-gray-300"
            >
              Preview
            </button>
            <button
              @click="confirmProposal"
              :disabled="state.loading"
              class="px-4 py-2 bg-primary text-white rounded disabled:bg-gray-300"
            >
              Confirm
            </button>
            <button
              @click="discardProposal"
              :disabled="state.loading"
              class="px-4 py-2 bg-red-600 text-white rounded disabled:bg-gray-300"
            >
              Discard
            </button>
            <button
              @click="close"
              :disabled="state.loading"
              class="px-4 py-2 bg-gray-500 text-white rounded disabled:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, computed } from 'vue';
import firebase from 'firebase/app';
import 'firebase/functions';
import { appConfig } from '../../../utils';

export default {
  name: 'ProposeTagsModal',
  setup() {
    let db = null;
    let store = null;
    let toast = null;

    const state = reactive({
      open: true,
      loading: false,
      loadingMessage: '',
      error: null,
      pageItem: null,
      rowItem: null,
      documents: [],
      selectedDocumentId: '',
      proposalActive: false,
      allowlist: [],
      sampleValues: {},
      reviewData: [],
      prepareResult: null
    });

    const formTypeName = computed(() => state.pageItem?.Name || state.pageItem?.NameShort || 'Unknown');
    const revisionNumber = computed(() => state.rowItem?.Revision || 'Unknown');
    const selectedDocumentLabel = computed(() => {
      const doc = state.documents.find(d => d.id === state.selectedDocumentId);
      return doc?.label || state.selectedDocumentId;
    });

    const checkedRowsCount = computed(() => state.reviewData.filter(r => r.checked).length);
    const allChecked = computed(() => state.reviewData.length > 0 && state.reviewData.every(r => r.checked));

    const open = async ({ pageItem, rowItem, db: _db, store: _store, toast: _toast }) => {
      db = _db;
      store = _store;
      toast = _toast;

      state.open = true;
      state.loading = true;
      state.loadingMessage = 'Loading documents...';
      state.error = null;
      state.pageItem = pageItem;
      state.rowItem = rowItem;
      state.documents = [];
      state.selectedDocumentId = '';
      state.proposalActive = false;
      state.reviewData = [];
      state.allowlist = [];
      state.sampleValues = {};
      state.prepareResult = null;

      try {
        // Check if there's a .docx source (GeneratableFile or File)
        const hasGeneratableFile = rowItem.GeneratableFile && 
                                   typeof rowItem.GeneratableFile === 'string' && 
                                   rowItem.GeneratableFile.toLowerCase().endsWith('.docx');
        const hasFile = rowItem.File && 
                       typeof rowItem.File === 'string' && 
                       rowItem.File.toLowerCase().endsWith('.docx');

        if (!hasGeneratableFile && !hasFile) {
          throw new Error('No .docx template file found for this revision. Please upload a template file first.');
        }

        await Promise.all([
          db.store('Database/Documents', store),
          db.store('Database/MilitaryDocuments', store)
        ]);

        const allDocuments = [
          ...Object.values(store.state['Database/Documents'] || {}),
          ...Object.values(store.state['Database/MilitaryDocuments'] || {})
        ];

        const matchingDocuments = allDocuments
          .filter(doc => doc.TypeID === pageItem.ID && doc.ID && doc.Number)
          .map(doc => ({
            id: doc.ID,
            label: `${doc.Number} - ${doc.Title || 'Untitled'}`
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        state.documents = matchingDocuments;
        state.loading = false;
      } catch (error) {
        console.error('[ProposeTagsModal] Error loading documents:', error);
        state.error = error.message || 'Failed to load documents';
        state.loading = false;
      }
    };

    const close = () => {
      state.open = false;
      state.pageItem = null;
      state.rowItem = null;
      state.documents = [];
      state.selectedDocumentId = '';
      state.proposalActive = false;
      state.reviewData = [];
      state.allowlist = [];
      state.sampleValues = {};
      state.prepareResult = null;
    };

    const startProposal = async () => {
      if (!state.selectedDocumentId) {
        toast.error('Please select a sample document');
        return;
      }

      state.loading = true;
      state.loadingMessage = 'Preparing template context...';

      try {
        const databaseURL = appConfig?.firebaseConfig?.databaseURL;
        if (!databaseURL) {
          throw new Error('Database configuration is missing');
        }

        const typeId = state.pageItem.ID;
        const typeRevisionId = state.rowItem.ID;
        const sampleDocumentId = state.selectedDocumentId;

        // Step 1: prepareTemplateTagContext
        const prepareCallable = firebase.app().functions().httpsCallable('prepareTemplateTagContext');
        state.loadingMessage = 'Preparing context...';
        const prepareResult = await prepareCallable({
          typeId,
          typeRevisionId,
          sampleDocumentId,
          databaseURL
        });

        if (!prepareResult.data) {
          throw new Error('Failed to prepare template context');
        }

        state.prepareResult = prepareResult.data;
        state.allowlist = prepareResult.data.allowlist || [];
        state.sampleValues = prepareResult.data.sampleValues || {};
        

        // Step 2: proposeTemplateTags
        const proposeCallable = firebase.app().functions().httpsCallable('proposeTemplateTags');
        state.loadingMessage = 'Generating AI tag proposals...';
        const proposeResult = await proposeCallable({
          typeId,
          typeRevisionId,
          sampleDocumentId,
          databaseURL,
          templatePlainText: prepareResult.data.templatePlainText,
          allowlist: prepareResult.data.allowlist,
          sampleValues: prepareResult.data.sampleValues,
          sourceFilename: prepareResult.data.sourceFilename,
          sourceKind: prepareResult.data.sourceKind
        });

        if (!proposeResult.data) {
          throw new Error('Failed to propose template tags');
        }

        const replacements = proposeResult.data.replacements || [];
        const rejected = proposeResult.data.rejected || [];

        

        // Step 3: applyTemplateTagMapping
        const applyCallable = firebase.app().functions().httpsCallable('applyTemplateTagMapping');
        state.loadingMessage = 'Applying tag mapping...';
        const applyResult = await applyCallable({
          typeId,
          typeRevisionId,
          sampleDocumentId,
          mapping: replacements,
          databaseURL
        });

        if (!applyResult.data?.success) {
          throw new Error(applyResult.data?.error || 'Failed to apply tag mapping');
        }

        

        // Build review table from apply result
        const reviewRows = buildReviewTable(
          replacements,
          rejected,
          applyResult.data.proposedMappingStatus,
          applyResult.data.rejected || []
        );

        state.reviewData = reviewRows;
        state.proposalActive = true;
        state.loading = false;
        toast.success('Template tags proposed successfully');
      } catch (error) {
        console.error('[ProposeTagsModal] Error during proposal:', error);
        state.error = error.message || 'Failed to propose template tags';
        state.loading = false;
        toast.error(state.error);
      }
    };

    const buildReviewTable = (replacements, proposeRejected, mappingStatus, applyRejected) => {
      const rows = [];
      
      // Process applied tags from mappingStatus.byPart
      if (mappingStatus?.byPart) {
        for (const [part, results] of Object.entries(mappingStatus.byPart)) {
          const partName = part.replace('word/', '').replace('.xml', '');
          const partResults = Array.isArray(results) ? results : Object.values(results || {});
          for (const result of partResults) {
            rows.push({
              findText: result.findText,
              fieldPath: result.fieldPath,
              sampleValue: result.sampleValue,
              confidence: replacements.find(r => r.findText === result.findText && r.fieldPath === result.fieldPath)?.confidence,
              status: result.status,
              part: partName,
              checked: result.status === 'applied' || result.status === 'skipped'
            });
          }
        }
      }

      // Add rejected tags from apply
      for (const rejected of applyRejected) {
        rows.push({
          findText: rejected.findText || rejected.fieldPath || '—',
          fieldPath: rejected.fieldPath || '',
          sampleValue: state.sampleValues[rejected.fieldPath] || '—',
          confidence: rejected.confidence,
          status: 'rejected',
          part: '—',
          checked: false
        });
      }

      // Add rejected tags from propose
      for (const rejected of proposeRejected) {
        if (!rows.some(r => r.findText === rejected.findText && r.fieldPath === rejected.fieldPath)) {
          rows.push({
            findText: rejected.findText || rejected.fieldPath || '—',
            fieldPath: rejected.fieldPath || '',
            sampleValue: state.sampleValues[rejected.fieldPath] || '—',
            confidence: rejected.confidence,
            status: 'rejected',
            part: '—',
            checked: false
          });
        }
      }

      // Add unused allowlist paths as warnings
      if (mappingStatus?.unusedAllowlistPaths) {
        for (const path of mappingStatus.unusedAllowlistPaths) {
          if (!rows.some(r => r.fieldPath === path)) {
            rows.push({
              findText: '—',
              fieldPath: path,
              sampleValue: state.sampleValues[path] || '—',
              confidence: null,
              status: 'warning',
              part: '—',
              checked: false
            });
          }
        }
      }

      return rows;
    };

    const reapplyMapping = async () => {
      state.loading = true;
      state.loadingMessage = 'Re-applying mapping...';

      try {
        const databaseURL = appConfig?.firebaseConfig?.databaseURL;
        if (!databaseURL) {
          throw new Error('Database configuration is missing');
        }

        const typeId = state.pageItem.ID;
        const typeRevisionId = state.rowItem.ID;
        const sampleDocumentId = state.selectedDocumentId;

        // Build mapping from checked rows
        const mapping = state.reviewData
          .filter(row => row.checked && row.findText !== '—')
          .map(row => ({
            findText: row.findText,
            fieldPath: row.fieldPath,
            occurrence: 1,
            confidence: row.confidence,
            reason: row.status || 'user-selected'
          }));

        const applyCallable = firebase.app().functions().httpsCallable('applyTemplateTagMapping');
        const applyResult = await applyCallable({
          typeId,
          typeRevisionId,
          sampleDocumentId,
          mapping,
          databaseURL
        });

        if (!applyResult.data?.success) {
          throw new Error(applyResult.data?.error || 'Failed to re-apply mapping');
        }

        

        // Rebuild review table from apply result
        const reviewRows = buildReviewTable(
          mapping,
          [],
          applyResult.data.proposedMappingStatus,
          applyResult.data.rejected || []
        );

        state.reviewData = reviewRows;
        state.loading = false;
        toast.success('Mapping re-applied successfully');
      } catch (error) {
        console.error('[ProposeTagsModal] Error re-applying mapping:', error);
        state.loading = false;
        toast.error(error.message || 'Failed to re-apply mapping');
      }
    };

    const onPathChange = () => {
      // Path was changed, user should re-apply to see updated results
    };

    const previewTemplate = async () => {
      state.loading = true;
      state.loadingMessage = 'Generating preview...';

      try {
        const databaseURL = appConfig?.firebaseConfig?.databaseURL;
        if (!databaseURL) {
          throw new Error('Database configuration is missing');
        }

        const typeId = state.pageItem.ID;
        const typeRevisionId = state.rowItem.ID;
        const sampleDocumentId = state.selectedDocumentId;

        const previewCallable = firebase.app().functions().httpsCallable('previewProposedTemplate');
        const previewResult = await previewCallable({
          typeId,
          typeRevisionId,
          sampleDocumentId,
          databaseURL
        });

        if (!previewResult.data?.success) {
          throw new Error(previewResult.data?.error || 'Failed to generate preview');
        }

        const previewPath = previewResult.data.previewPath;
        if (!previewPath) {
          throw new Error('No preview path returned');
        }

        toast.info('Downloading preview...');
        const url = await db.download(previewPath);
        window.open(url, '_blank');

        state.loading = false;
        toast.success('Preview generated successfully');
      } catch (error) {
        console.error('[ProposeTagsModal] Error generating preview:', error);
        state.loading = false;
        toast.error(error.message || 'Failed to generate preview');
      }
    };

    const confirmProposal = async () => {
      state.loading = true;
      state.loadingMessage = 'Confirming proposal...';

      try {
        const databaseURL = appConfig?.firebaseConfig?.databaseURL;
        if (!databaseURL) {
          throw new Error('Database configuration is missing');
        }

        const typeId = state.pageItem.ID;
        const typeRevisionId = state.rowItem.ID;

        const confirmCallable = firebase.app().functions().httpsCallable('confirmProposedTemplate');
        const confirmResult = await confirmCallable({
          typeId,
          typeRevisionId,
          databaseURL
        });

        if (!confirmResult.data?.success) {
          throw new Error(confirmResult.data?.error || 'Failed to confirm proposal');
        }

        state.loading = false;
        toast.success('Template tags confirmed successfully. The GeneratableFile has been updated.');
        close();
      } catch (error) {
        console.error('[ProposeTagsModal] Error confirming proposal:', error);
        state.loading = false;
        toast.error(error.message || 'Failed to confirm proposal');
      }
    };

    const discardProposal = async () => {
      state.loading = true;
      state.loadingMessage = 'Discarding proposal...';

      try {
        const databaseURL = appConfig?.firebaseConfig?.databaseURL;
        if (!databaseURL) {
          throw new Error('Database configuration is missing');
        }

        const typeId = state.pageItem.ID;
        const typeRevisionId = state.rowItem.ID;

        const discardCallable = firebase.app().functions().httpsCallable('discardProposedTemplate');
        const discardResult = await discardCallable({
          typeId,
          typeRevisionId,
          databaseURL
        });

        if (!discardResult.data?.success) {
          throw new Error(discardResult.data?.error || 'Failed to discard proposal');
        }

        state.loading = false;
        toast.info('Proposal discarded');
        close();
      } catch (error) {
        console.error('[ProposeTagsModal] Error discarding proposal:', error);
        state.loading = false;
        toast.error(error.message || 'Failed to discard proposal');
      }
    };

    const toggleAll = () => {
      const newChecked = !allChecked.value;
      state.reviewData.forEach(row => {
        row.checked = newChecked;
      });
    };

    return {
      state,
      formTypeName,
      revisionNumber,
      selectedDocumentLabel,
      checkedRowsCount,
      allChecked,
      open,
      close,
      startProposal,
      reapplyMapping,
      onPathChange,
      previewTemplate,
      confirmProposal,
      discardProposal,
      toggleAll
    };
  }
};
</script>
